import { useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import {
    isVaultTemporarilyUnavailable,
    BsMetaUtil,
    buildExternalRewardsByVault,
    convertExternalRewardsToAggregatedRewards,
    LendingVaultExpanded,
    MAX_LOANS_PAGE_SIZE,
    MAXIMUM_PAGE_SIZE,
    RewardMint,
    StakeSortType,
    UserExternalRewardsInfoExpanded,
    UserLendingVaultFilter,
    UserVaultExternalRewardsPnlResponse,
    UserVaultGroup,
    UserVaultPositionExpanded,
    UserVaultPositionStatus,
    UserVaultsFilter,
    useGetUserLpPositionAmounts,
    useLendingVaultPositionsGroupedByMint,
    useLendVaults,
    useLoopscaleWalletContext,
    useTokenListContext,
    useUserLendingVaultPositions,
    useUserLendingVaultPositionsPaginated,
    useUserVaultDepositSummarys,
    useUserVaultPositionExternalRewards,
    useUserVaults,
    userVaultGroupTableKey
} from "@bridgesplit/abf-react";
import {
    createRows,
    TABLE_CTA_COLUMN_KEY,
    Column,
    MEDIA,
    MobilePortfolioCards,
    MobilePortfolioPagination,
    MobilePortfolioRowConfig,
    Row,
    TableColumn,
    TableKey,
    Table,
    Text,
    Icon
} from "@bridgesplit/ui";
import {
    ALL_VALUE,
    AllValueType,
    EMPTY_STRING,
    filterUndefined,
    formatUsd,
    isAll,
    SortDirection
} from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { useChunkedPaginationController, useClampPaginationOnCountChange } from "app/api/shared";
import { useLendingPositionPagination } from "app/api/vaults";
import { getLendingVaultPath, TableCardWrapper, TokenImage } from "app/components/common";
import { RateDisplay } from "app/components/points/app/common";
import { VaultPositionApy } from "app/components/vault/position/VaultPositionCard";
import { AppDialog, useAppDialog } from "app/utils";
import { UnclaimedPositionCard } from "app/components/vault/position/UnclaimedPositionCard";

import { hasClaimableExternalRewards, LendVaultStats } from "./stats";
import { SectionCollapse, SectionSkeleton } from "./PortfolioSections";
import {
    CELL_WIDTH_DEFAULTS,
    getDefaultPaginationController,
    GROUP_CTA_CELL,
    GROUP_HEADER_CELL_TOOLTIPS,
    GroupNameCell,
    PairTokenValueCell,
    RowActionsIconButton
} from "./common";
import { ChipStatPair, PortfolioAllChip, PortfolioTokenChip } from "./mobileCards";

const PAGE_SIZE = 5;

const LEND_VAULT_ROW_CONFIG: MobilePortfolioRowConfig = {
    headerDataIndex: "name",
    primaryValueDataIndex: "positionValueUsd",
    statDataIndexes: ["daily_earnings", "earnings", "apy"]
};

interface LendVaultSectionProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean;
}

export default function LendVaultSection({ isExpanded, isUsdValueLoading }: LendVaultSectionProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    const pagination = useChunkedPaginationController<UserVaultGroup, UserVaultsFilter>(
        {
            userAddress: undefined,
            principalMints: undefined,
            vaultAddresses: undefined,
            lpMints: undefined,
            status: UserVaultPositionStatus.Active,
            sortDirection: SortDirection.Desc
        },
        PAGE_SIZE
    );
    const {
        groups,
        totalGroups,
        aggregate: desktopAggregate,
        isLoading
    } = useUserVaults(pagination.backendPagination, isMobile);
    useClampPaginationOnCountChange(pagination.clampPageToCount, totalGroups);

    const visibleGroups = useMemo(() => pagination.visibleData(groups) ?? [], [groups, pagination]);
    const visiblePrincipalMints = useMemo(() => visibleGroups.map((g) => g.principalMint), [visibleGroups]);

    const { positionsByMint, isLoading: detailsLoading } = useLendingVaultPositionsGroupedByMint(
        visiblePrincipalMints,
        {
            skip: isMobile
        }
    );

    const mobile = useMobileLendVaultView(isMobile);
    const aggregate = isMobile ? mobile.aggregate : desktopAggregate;

    const { data: userPositionPnl } = useUserVaultPositionExternalRewards();
    const externalRewardsByVault = useMemo(() => buildExternalRewardsByVault(userPositionPnl), [userPositionPnl]);

    const unclaimedRewardsCards = useUnclaimedRewardsCards(userPositionPnl);

    const tableRows = useMemo(() => createRows<UserVaultGroup>(visibleGroups, userVaultGroupTableKey), [visibleGroups]);

    const groupedColumnDefs = useMemo(() => getGroupColumnDefs(), []);
    const itemColumnDefs = useMemo(() => getVaultDetailColumnDefs(externalRewardsByVault), [externalRewardsByVault]);

    if (isUsdValueLoading) return <SectionSkeleton />;

    return (
        <SectionCollapse isExpanded={isExpanded}>
            <LendVaultStats aggregate={aggregate} />
            <TableCardWrapper flushOnMobile>
                {isMobile ? (
                    <MobilePortfolioCards<UserVaultGroup, UserVaultPositionExpanded>
                        filterLabel="Filter by asset"
                        activeFilter={mobile.activeFilter}
                        onFilterChange={mobile.onFilterChange}
                        groups={mobile.chipRows}
                        renderChip={(row) => <LendVaultChipContent group={row?.data} />}
                        groupsLoading={mobile.allGroupsLoading}
                        positions={mobile.positionRows}
                        positionColumns={itemColumnDefs}
                        rowConfig={LEND_VAULT_ROW_CONFIG}
                        onPositionNavigatePath={(p) => {
                            const path = getLendingVaultPath(p.vaultExpanded);
                            return path === "#" ? undefined : path;
                        }}
                        positionsLoading={mobile.positionsLoading}
                        pagination={mobile.pagination}
                        hasHealth={false}
                        hasSelect={false}
                        emptyPlaceholder={`No active ${COPY.LEND_VAULTS_TERM_PLURAL.toLowerCase()} positions`}
                        listLabel={mobile.listLabel}
                        allCard={
                            <PortfolioAllChip
                                loading={!aggregate}
                                count={aggregate?.count}
                                valueUsd={aggregate?.totalValueUsd}
                                assetCount={mobile.allGroupsTotal}
                                stats={
                                    <LendVaultChipStats
                                        dailyEarningsUsd={aggregate?.expected24hInterestUsd}
                                        apy={aggregate?.wAvgTotalApy}
                                        loading={!aggregate}
                                    />
                                }
                            />
                        }
                    />
                ) : (
                    <Table<UserVaultGroup, UserVaultPositionExpanded>
                        rows={tableRows}
                        columns={groupedColumnDefs}
                        grouping={{
                            columns: itemColumnDefs,
                            getItems: (groupKey) =>
                                typeof groupKey === "string" ? positionsByMint?.get(groupKey) : undefined,
                            getDetailKey: (p) => p.address,
                            isDetailsLoading: detailsLoading,
                            onDetailRowNavigatePath: (detail) => {
                                const path = getLendingVaultPath(detail.vaultExpanded);
                                return path === "#" ? undefined : path;
                            }
                        }}
                        paginationProps={{
                            type: "api",
                            pageSize: pagination.pageSize,
                            totalDataCount: totalGroups,
                            controller: getDefaultPaginationController(pagination)
                        }}
                        loading={isLoading}
                        emptyPlaceholder={`No active ${COPY.LEND_VAULTS_TERM_PLURAL.toLowerCase()} positions`}
                        showViewToggle
                        viewToggleStorageKey="PORTFOLIO_LEND_VAULT_VIEW"
                    />
                )}
            </TableCardWrapper>
            {unclaimedRewardsCards.length > 0 && (
                <Column spacing={1}>
                    {unclaimedRewardsCards.map((card) => (
                        <UnclaimedPositionCard
                            key={card.vaultAddress}
                            query={MEDIA.LG}
                            vaultExpanded={card.vault}
                            rewards={card.rewards}
                            lastWithdrawalTimestamp={card.lastWithdrawalTimestamp}
                        />
                    ))}
                </Column>
            )}
        </SectionCollapse>
    );
}

function useMobileLendVaultView(isMobile: boolean) {
    const { getMetadata } = useTokenListContext();
    const [activeFilter, setActiveFilter] = useState<AllValueType | TableKey>(ALL_VALUE);

    const {
        groups: allGroups,
        totalGroups: allGroupsTotal,
        aggregate,
        isLoading: allGroupsLoading
    } = useUserVaults(
        {
            status: UserVaultPositionStatus.Active,
            sortDirection: SortDirection.Desc,
            page: 0,
            pageSize: MAX_LOANS_PAGE_SIZE
        },
        !isMobile
    );

    const positionsPagination = useLendingPositionPagination();
    const { visibleData, clampPageToCount } = positionsPagination;
    const {
        positions: positionsChunk,
        totalCount: positionsTotal,
        isLoading: positionsLoading
    } = useUserLendingVaultPositionsPaginated({
        pagination: positionsPagination.backendPagination,
        principalGroupKey: activeFilter,
        skip: !isMobile
    });
    useClampPaginationOnCountChange(clampPageToCount, positionsTotal);

    const visiblePositions = useMemo(() => visibleData(positionsChunk), [positionsChunk, visibleData]);

    const chipRows = useMemo(() => createRows<UserVaultGroup>(allGroups ?? [], userVaultGroupTableKey), [allGroups]);
    const positionRows = useMemo(
        () =>
            visiblePositions ? createRows<UserVaultPositionExpanded>(visiblePositions, (p) => p.address) : undefined,
        [visiblePositions]
    );

    const onFilterChange = (next: AllValueType | TableKey) => {
        setActiveFilter(next);
        positionsPagination.resetPagination();
    };

    const activeGroupSymbol = useMemo(() => {
        if (isAll(activeFilter)) return undefined;
        const metadata = getMetadata(String(activeFilter));
        return metadata ? BsMetaUtil.getSymbol(metadata) : undefined;
    }, [activeFilter, getMetadata]);
    const listLabel = `${activeGroupSymbol ?? "All"} positions`;

    const pagination: MobilePortfolioPagination = {
        page: positionsPagination.page,
        setPage: positionsPagination.setPage,
        pageSize: positionsPagination.pageSize,
        totalCount: positionsTotal
    };

    return {
        activeFilter,
        onFilterChange,
        chipRows,
        allGroupsLoading,
        allGroupsTotal,
        aggregate,
        positionRows,
        positionsLoading,
        pagination,
        listLabel
    };
}

function LendVaultChipStats({
    dailyEarningsUsd,
    apy,
    loading
}: {
    dailyEarningsUsd: number | undefined;
    apy: number | undefined;
    loading?: boolean;
}) {
    return (
        <ChipStatPair
            loading={loading}
            left={<Text variant="body1">{formatUsd(dailyEarningsUsd)}</Text>}
            right={<RateDisplay apy={apy} variant="body1" color="body" underline={false} />}
        />
    );
}

function LendVaultChipContent({ group }: { group: UserVaultGroup | undefined }) {
    return (
        <PortfolioTokenChip
            metadata={group?.principalMetadata}
            count={group?.aggregate.count}
            valueUsd={group?.aggregate.totalValueUsd}
            tokenAmount={group?.totalValueNative}
            stats={
                <LendVaultChipStats
                    dailyEarningsUsd={group?.aggregate.expected24hInterestUsd}
                    apy={group?.aggregate.wAvgTotalApy}
                    loading={!group}
                />
            }
        />
    );
}

interface UnclaimedRewardsCard {
    vaultAddress: string;
    vault: LendingVaultExpanded | undefined;
    rewards: Record<RewardMint, UserExternalRewardsInfoExpanded>;
    lastWithdrawalTimestamp: number | undefined;
}

/** Vaults the user has fully withdrawn from but still has unclaimed rewards on are excluded from the
 *  active-only grouped table, so surface them as standalone cards below it (staging parity). */
function useUnclaimedRewardsCards(
    userPositionPnl: UserVaultExternalRewardsPnlResponse | null | undefined
): UnclaimedRewardsCard[] {
    const {
        data: { userWalletAddress: wallet }
    } = useLoopscaleWalletContext();

    const activePositionsFilter = useMemo(
        (): UserLendingVaultFilter => ({
            userAddress: wallet ?? "",
            principalMints: [],
            vaultAddresses: [],
            lpMints: [],
            sort: StakeSortType.AmountStaked,
            sortDirection: SortDirection.Asc,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE,
            status: UserVaultPositionStatus.Active
        }),
        [wallet]
    );
    const { data: activePositions } = useUserLendingVaultPositions(activePositionsFilter, !wallet);
    const { data: depositSummarys } = useUserVaultDepositSummarys();

    const unclaimedVaultAddresses = useMemo(() => {
        if (!userPositionPnl || !activePositions) return [];
        const activeVaultAddresses = new Set(activePositions.map((p) => p.vaultExpanded.vault.address));
        return Object.keys(userPositionPnl).filter(
            (vaultAddress) =>
                !activeVaultAddresses.has(vaultAddress) && isVaultRewardsUnclaimed(userPositionPnl, vaultAddress)
        );
    }, [userPositionPnl, activePositions]);

    const { data: unclaimedVaults } = useLendVaults({
        filter: { vaultAddresses: unclaimedVaultAddresses, includeRewards: true },
        skip: unclaimedVaultAddresses.length === 0,
        skipTimelocks: true
    });

    return useMemo(() => {
        if (!unclaimedVaults) return [];
        const vaultByAddress = new Map(unclaimedVaults.map((v) => [v.vault.address, v]));
        return unclaimedVaultAddresses
            .map((vaultAddress): UnclaimedRewardsCard | undefined => {
                const vault = vaultByAddress.get(vaultAddress);
                const rewards = convertExternalRewardsToAggregatedRewards(vault, userPositionPnl)?.[vaultAddress];
                if (!rewards) return undefined;
                const claimable = Object.fromEntries(
                    Object.entries(rewards).filter(([, reward]) => reward.pendingRewards > 0)
                );
                if (!Object.keys(claimable).length) return undefined;
                return {
                    vaultAddress,
                    vault,
                    rewards: claimable,
                    lastWithdrawalTimestamp: depositSummarys?.[vaultAddress]?.lastInteractedTime
                };
            })
            .filter(filterUndefined);
    }, [unclaimedVaults, unclaimedVaultAddresses, userPositionPnl, depositSummarys]);
}

function isVaultRewardsUnclaimed(userPositionPnl: UserVaultExternalRewardsPnlResponse, vaultAddress: string): boolean {
    const vaultRewards = userPositionPnl[vaultAddress];
    if (!vaultRewards) return false;
    return Object.values(vaultRewards).some((stakeRewards) =>
        Object.values(stakeRewards).some((reward) => reward.pendingRewards > 0)
    );
}

function getGroupColumnDefs(): TableColumn<UserVaultGroup>[] {
    return [
        {
            dataIndex: "asset",
            title: "Asset",
            render: (row) => <GroupNameCell metadata={row.data.principalMetadata} count={row.data.aggregate.count} />
        },
        {
            dataIndex: "position",
            title: "Position value",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    usdAmount={row.data.aggregate.totalValueUsd}
                    tokenAmount={row.data.totalValueNative}
                    metadata={row.data.principalMetadata}
                    isRawTokenAmount
                />
            )
        },
        {
            dataIndex: "daily_earnings",
            title: "Daily earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <PairTokenValueCell usdAmount={row.data.aggregate.expected24hInterestUsd} />
        },
        {
            dataIndex: "earnings",
            title: "Total earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <PairTokenValueCell usdAmount={row.data.aggregate.totalEarnedUsd} />
        },
        {
            dataIndex: "apy",
            title: "APY",
            tooltip: GROUP_HEADER_CELL_TOOLTIPS.vaults.apy,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) => (
                <RateDisplay apy={row.data.aggregate.wAvgTotalApy} variant="body1" color="body" underline={false} />
            )
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: GROUP_CTA_CELL
        }
    ];
}

function getVaultDetailColumnDefs(
    externalRewardsByVault: Record<string, Record<RewardMint, UserExternalRewardsInfoExpanded>> | undefined
): TableColumn<UserVaultPositionExpanded>[] {
    return [
        {
            dataIndex: "name",
            title: "Name",
            render: (row) => <NameCell vaultExpanded={row.data.vaultExpanded} />
        },
        {
            dataIndex: "positionValueUsd",
            title: "Position value",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => <PositionCell vaultPosition={row.data} />
        },
        {
            dataIndex: "daily_earnings",
            title: "Daily earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <DailyEarningsCell vaultPosition={row.data} />
        },
        {
            dataIndex: "earnings",
            title: "Total earnings",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) => <EarningsCell vaultPosition={row.data} />
        },

        {
            dataIndex: "apy",
            title: "APY",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.default,
            render: (row) =>
                isVaultTemporarilyUnavailable(row.data.vaultExpanded) ? (
                    <Text color="disabled">—</Text>
                ) : (
                    <VaultPositionApy vaultPosition={row.data} hideApyText />
                )
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: (row) => (
                <CtaCell
                    vaultExpanded={row.data.vaultExpanded}
                    showClaimRewards={hasClaimableExternalRewards(externalRewardsByVault?.[row.data.address])}
                />
            )
        }
    ];
}

/* Vault cells */
function NameCell({ vaultExpanded }: { vaultExpanded: LendingVaultExpanded }) {
    return (
        <Row spacing={1}>
            <TokenImage size="sm" metadata={vaultExpanded.principalMetadata} />
            <Text variant="body1">{BsMetaUtil.getSymbol(vaultExpanded.principalMetadata)}</Text>
            <Text variant="body2" color="caption">
                {vaultExpanded.vaultMetadata?.name}
            </Text>
        </Row>
    );
}

function PositionCell({ vaultPosition }: { vaultPosition: UserVaultPositionExpanded }) {
    const { getMetadata } = useTokenListContext();
    const { totalAmount, lpCollateralDeposits } = useGetUserLpPositionAmounts({
        userPosition: vaultPosition,
        vaultExpanded: vaultPosition.vaultExpanded
    });

    const tokenAmount = totalAmount.amountPrincipal + lpCollateralDeposits.amountPrincipal;
    const usdAmount = totalAmount.amountUsd + lpCollateralDeposits.amountUsd;
    const metadata = getMetadata(vaultPosition.vaultExpanded.vault.principalMint);

    return <PairTokenValueCell tokenAmount={tokenAmount} usdAmount={usdAmount} metadata={metadata} />;
}

function DailyEarningsCell({ vaultPosition }: { vaultPosition: UserVaultPositionExpanded }) {
    const { totalApy } = vaultPosition;
    const apy = totalApy ?? 0;

    const { totalAmount } = useGetUserLpPositionAmounts({
        userPosition: vaultPosition,
        vaultExpanded: vaultPosition.vaultExpanded
    });
    const perDayUsd = (totalAmount.amountUsd * apy) / 365;

    return <PairTokenValueCell usdAmount={Math.abs(perDayUsd)} />;
}

function EarningsCell({ vaultPosition }: { vaultPosition: UserVaultPositionExpanded }) {
    return <PairTokenValueCell usdAmount={vaultPosition.pnlUsd} />;
}

function CtaCell({
    vaultExpanded,
    showClaimRewards
}: {
    vaultExpanded: LendingVaultExpanded;
    showClaimRewards: boolean;
}) {
    const { open: openDialog } = useAppDialog();

    const items = useMemo(
        () => [
            {
                text: "Deposit",
                icon: <Icon type="arrow-down" />,
                onClick: () => openDialog(AppDialog.VaultManageEscrow, { vaultExpanded, side: "deposit" })
            },
            {
                text: "Withdraw",
                icon: <Icon type="arrow-diagonal" />,

                onClick: () => openDialog(AppDialog.VaultManageEscrow, { vaultExpanded, side: "withdraw" })
            },
            ...(showClaimRewards
                ? [
                      {
                          text: "Claim Rewards",
                          icon: <Icon type="sparkles" />,
                          onClick: () => openDialog(AppDialog.VaultClaimRewards, { vaultExpanded })
                      }
                  ]
                : [])
        ],
        [openDialog, showClaimRewards, vaultExpanded]
    );

    return <RowActionsIconButton items={items} />;
}
